import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';

interface ConversationProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const Conversation = forwardRef<HTMLDivElement, ConversationProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col overflow-y-auto', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Conversation.displayName = 'Conversation';

interface ConversationContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const ConversationContent = forwardRef<HTMLDivElement, ConversationContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        {children}
      </div>
    );
  },
);
ConversationContent.displayName = 'ConversationContent';

interface MessageProps {
  from: 'user' | 'assistant' | 'system';
  children: React.ReactNode;
  className?: string;
}

export const Message: React.FC<MessageProps> = ({ from, children, className }) => {
  const isUser = from === 'user';
  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start',
        className,
      )}
    >
      <div className="flex flex-col gap-1 max-w-full">{children}</div>
    </div>
  );
};

interface MessageContentProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <span className={cn('whitespace-pre-wrap break-words', className)} {...props}>
      {children}
    </span>
  );
};

interface MessageResponseProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const MessageResponse: React.FC<MessageResponseProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        'mr-auto bg-white text-gray-500 text-xs px-3 py-2 rounded-2xl rounded-tl-none border border-gray-200/60 max-w-[85%] flex items-center gap-1',
        className,
      )}
      {...props}
    >
      <span className="inline-flex gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" />
      </span>
      {children && <span className="ml-1">{children}</span>}
    </div>
  );
};